import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import Image from "next/image";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { EnvService } from "@/services/Environment/Environment";
import { ConfigFormRenderer } from "../../Form/ConfigFormRenderer";
import { ModalSectionLayout } from "./ModalSectionLayout";
import { Skeleton } from "../../shadcn/skeleton";
import { Button } from "../../shadcn/button";
import { SpotToast } from "../../SpotToast";
import { ArrowLeft, KeyRound, RefreshCw, PlugZap, Plug } from "lucide-react";
import { DialogHeader, DialogTitle } from "../../shadcn/dialog";
import { DeviceAuthConnect } from "./DeviceAuthConnect";
import { OAuthAppForm } from "./OAuthAppForm";
import type { ActiveCredential } from "./types";
import { hasOAuthAppForm, isOAuthAppComplete } from "./utils";

export function InModalCredentialEdit({
  cred,
  groupId,
  onBack,
  onClose,
  embedded,
  onReconnect,
}: {
  cred: ActiveCredential;
  groupId: string;
  onBack: () => void;
  onClose: () => void;
  embedded?: boolean;
  onReconnect?: (
    cred: ActiveCredential,
    connectionConfig?: Record<string, unknown>
  ) => Promise<boolean>;
}) {
  const queryClient = useQueryClient();
  const [deviceReauth, setDeviceReauth] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const configEndpoint = cred.provider.config_url ?? "";
  const isOAuthApp = hasOAuthAppForm(cred.provider);
  // Only the non-secret fields come back; a blank secret keeps the stored one.
  const oauthApp = isOAuthApp
    ? (cred.meta?.oauth_app as Record<string, unknown> | undefined)
    : undefined;
  const credentialPayloadEndpoint = `/groups/${groupId}/integrations/credentials/${cred.credentialId}`;
  const submitEndpoint = credentialPayloadEndpoint;

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ["integration-config", cred.integration.type, configEndpoint],
    queryFn: async () => {
      const response = await axios.post(`/api/backend${configEndpoint}`, {});
      return response.data;
    },
    enabled: !!configEndpoint,
  });

  const { data: existingPayload, isLoading: payloadLoading } = useQuery({
    queryKey: ["credential-payload", credentialPayloadEndpoint],
    queryFn: async () => {
      const response = await axios.get(
        `/api/backend${credentialPayloadEndpoint}`
      );
      return response.data?.payload as Record<string, unknown> | undefined;
    },
    enabled: cred.provider.auth_type === "secret_auth",
  });

  const initialValues = useMemo(() => {
    const values: Record<string, unknown> = {};
    if (configData?.config) {
      Object.keys(configData.config).forEach((key) => {
        const cfg = configData.config[key] as
          | Record<string, unknown>
          | undefined;
        values[key] = cfg?.default ?? "";
      });
    }
    const stored = existingPayload ?? oauthApp;
    if (stored) {
      Object.keys(stored).forEach((key) => {
        if (key in values || configData?.config?.[key]) {
          values[key] = stored[key];
        }
      });
    }
    return values;
  }, [configData, existingPayload, oauthApp]);

  const form = useForm({ defaultValues: initialValues, mode: "onChange" });

  useEffect(() => {
    if (configData?.config) {
      form.reset(initialValues);
    }
  }, [configData, existingPayload, form, initialValues]);

  const formValues = useWatch({ control: form.control });
  // The backend keeps the stored secret only for the same instance and client.
  const oauthAppUnchanged =
    !!oauthApp &&
    Object.entries(oauthApp).every(([key, value]) => formValues[key] === value);
  const oauthAppReady =
    !isOAuthApp ||
    (!!configData?.config &&
      isOAuthAppComplete(configData.config, formValues, oauthAppUnchanged));

  const handleReconnect = async () => {
    if (!onReconnect) return;
    setReconnecting(true);
    const started = await onReconnect(
      cred,
      isOAuthApp ? form.getValues() : undefined
    );
    if (!started) setReconnecting(false);
  };

  const submitMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      return axios.put(`/api/backend${submitEndpoint}`, { payload: formData });
    },
    onSuccess: () => {
      SpotToast.success(
        `${cred.integration.display_name} updated successfully.`
      );
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
      onClose();
    },
    onError: (error: {
      response?: { data?: { message?: string } };
      message?: string;
    }) => {
      SpotToast.error(
        `Failed to update: ${error?.response?.data?.message || error?.message || "Unknown error"}`
      );
    },
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      await axios.post(
        `/api/backend/groups/${groupId}/integrations/credentials/${cred.credentialId}/refresh`
      );
    },
    onSuccess: () => {
      SpotToast.success("Credential refreshed successfully.");
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.INTEGRATIONS] });
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.INTEGRATIONS_CONFIG],
      });
    },
    onError: (error: {
      response?: { data?: { message?: string } };
      message?: string;
    }) => {
      SpotToast.error(
        `Failed to refresh: ${error?.response?.data?.message || error?.message || "Unknown error"}`
      );
    },
  });

  const isLoading = configLoading || payloadLoading;

  if (embedded) {
    return (
      <ModalSectionLayout
        title="Credentials"
        description="Manage the credentials used for this connection."
        footer={
          cred.provider.auth_type === "secret_auth" && (
            <div className="flex items-center justify-end">
              <Button
                variant="default"
                onClick={() => submitMutation.mutate(form.getValues())}
                disabled={
                  isLoading ||
                  !!configError ||
                  submitMutation.status === "pending"
                }
              >
                {submitMutation.status === "pending" ? "Saving\u2026" : "Save"}
              </Button>
            </div>
          )
        }
      >
        <>
          {cred.provider.auth_type === "device_code" ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">
                  {cred.provider.display_name || "Device sign-in"}
                </h4>
                <p className="text-xs text-muted-foreground">
                  This credential holds a session captured by signing in with a
                  one-time code. Sign in again to replace it.
                </p>
              </div>
              {deviceReauth ? (
                <DeviceAuthConnect
                  integrationType={cred.integration.type}
                  displayName={cred.integration.display_name}
                  provider={cred.provider}
                  credentialId={cred.credentialId}
                  onSuccess={onClose}
                />
              ) : (
                <Button
                  onClick={() => setDeviceReauth(true)}
                  className="bg-zinc-900 text-white hover:bg-zinc-800"
                >
                  <PlugZap size={14} />
                  Sign in again
                </Button>
              )}
            </div>
          ) : cred.provider.auth_type !== "secret_auth" ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">OAuth Connection</h4>
                <p className="text-xs text-muted-foreground">
                  This credential is managed through an OAuth provider.
                  Reconnect to re-authorize or refresh to update the token.
                </p>
              </div>
              {isOAuthApp && configLoading && (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}
              {isOAuthApp && configData?.config && (
                <OAuthAppForm
                  provider={cred.provider}
                  integrationName={cred.integration.display_name}
                  config={configData.config}
                  form={form}
                  keepsSecret
                />
              )}
              {!!(cred.meta as Record<string, unknown>)?.last_refresh && (
                <p className="text-xs text-muted-foreground">
                  Last refreshed{" "}
                  {new Date(
                    (cred.meta as Record<string, unknown>)
                      .last_refresh as string
                  ).toLocaleString()}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleReconnect}
                  loading={reconnecting}
                  disabled={!oauthAppReady}
                  className="bg-zinc-900 text-white hover:bg-zinc-800"
                >
                  <PlugZap size={14} />
                  Reconnect
                </Button>
                <Button
                  variant="outline"
                  disabled={refreshMutation.status === "pending"}
                  onClick={() => refreshMutation.mutate()}
                >
                  <RefreshCw
                    size={14}
                    className={`${refreshMutation.status === "pending" ? "animate-spin" : ""}`}
                  />
                  {refreshMutation.status === "pending"
                    ? "Refreshing\u2026"
                    : "Refresh"}
                </Button>
              </div>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : configError ? (
            <p className="text-sm text-destructive">
              Failed to load configuration. Please try again.
            </p>
          ) : configData?.config ? (
            <div className="[&_label_span]:text-sm [&_label_span]:font-medium">
              <ConfigFormRenderer
                config={configData.config}
                form={form}
                getNodeById={() => undefined}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No configuration available.
            </p>
          )}
        </>
      </ModalSectionLayout>
    );
  }

  return (
    <>
      <DialogHeader className="px-6 py-5 border-b border-border/40">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={onBack}
          >
            <ArrowLeft size={16} />
          </Button>
          <div className="flex-shrink-0 w-8 h-8 rounded-md bg-background border border-border/40 flex items-center justify-center p-1.5 shadow-sm">
            {cred.integration.image ? (
              <Image
                width={20}
                height={20}
                alt={cred.integration.display_name}
                src={cred.integration.image}
                style={{ objectFit: "contain", width: "100%", height: "100%" }}
              />
            ) : (
              <Plug size={16} className="text-muted-foreground" />
            )}
          </div>
          <DialogTitle className="text-base font-semibold">
            Edit {cred.integration.display_name}
          </DialogTitle>
        </div>
      </DialogHeader>

      <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : configError ? (
          <p className="text-sm text-destructive">
            Failed to load configuration. Please try again.
          </p>
        ) : configData?.config ? (
          <ConfigFormRenderer
            config={configData.config}
            form={form}
            getNodeById={() => undefined}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            No configuration available.
          </p>
        )}
      </div>

      <div className="px-6 py-4 border-t border-border/40 flex items-center justify-end gap-2">
        <Button variant="ghost" onClick={onBack}>
          Cancel
        </Button>
        <Button
          onClick={() => submitMutation.mutate(form.getValues())}
          disabled={
            isLoading || !!configError || submitMutation.status === "pending"
          }
          className="bg-zinc-900 text-white hover:bg-zinc-800"
        >
          {submitMutation.status === "pending" ? "Saving\u2026" : "Save"}
        </Button>
      </div>
    </>
  );
}
